import Visibility from '@src/elements/Visibility';
import React, { useState, useEffect, startTransition } from 'react';
import {
  CtaFragment,
  HeroLargeFragment,
  Maybe,
  MediaVideoFragment
} from '@src/graphql/gql-types';
import styles from './HeroLarge.module.scss';
import HeroContent from '@src/components/HeroContent';
import { Container } from '@src/elements/Grid';
import { COLOR_LIME } from '@src/types/colors';
import MediaImage from '@src/components/MediaImage';
import { fill } from '@cloudinary/url-gen/actions/resize';
import { format } from '@cloudinary/url-gen/actions/delivery';
import { autoGood } from '@cloudinary/url-gen/qualifiers/quality';
import { cloudinaryClient } from '@src/services/cloudinary';
import { WordDictionary } from '@src/types/dictionary';
import { replaceStringWithDictionary } from '@src/utils/strings';
import { SunraySVG } from '@src/svgs';
import BoxIcon from '@src/elements/BoxIcon';
import { CloudinaryVideo } from '@cloudinary/url-gen';
import classNames from 'classnames';
import Head from 'next/head';
import { trim } from '@cloudinary/url-gen/actions/videoEdit';
import dynamic from 'next/dynamic';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

type HeroLarge = HeroLargeFragment & {
  isHomepage?: boolean;
  slate?: boolean;
  replacementDictionary?: WordDictionary;
  displayTourBookingButton?: boolean;
  isCommunity?: boolean;
};

const DesktopHeroContentContainer = ({
  primaryCta,
  headingText,
  descriptionText,
  displayTourBookingButton,
  isCommunity,
  mediaVideo,
  videoProgress,
  playing,
  setPlaying
}: {
  primaryCta: CtaFragment;
  headingText: Maybe<string>;
  descriptionText: Maybe<string>;
  displayTourBookingButton?: boolean;
  isCommunity?: boolean;
  mediaVideo?: MediaVideoFragment;
  videoProgress: number;
  playing: boolean;
  setPlaying: React.Dispatch<React.SetStateAction<boolean>>;
}) => (
  <Container containerWidth="fluid" className={styles.heroContentContainer}>
    <div className={styles.heroContentDesktop}>
      <div className={styles.heroContentLargeCard}>
        <HeroContent
          primaryCta={primaryCta as CtaFragment}
          heading={headingText || ''}
          description={descriptionText || ''}
          displayTourBookingButton={displayTourBookingButton}
          isCommunity={isCommunity}
        />

        <div className={styles.sunrayContainer}>
          <SunraySVG color={COLOR_LIME} />
        </div>
      </div>

      {mediaVideo && (
        <div className={styles.buttonContainer}>
          <PlayButton
            progress={videoProgress}
            playing={playing}
            setPlaying={setPlaying}
          />
        </div>
      )}
    </div>
  </Container>
);

const MobileHeroContentContainer = ({
  primaryCta,
  headingText,
  descriptionText,
  displayTourBookingButton,
  isCommunity,
  mediaVideo,
  videoProgress,
  playing,
  setPlaying
}: {
  primaryCta: CtaFragment;
  headingText: Maybe<string>;
  descriptionText: Maybe<string>;
  displayTourBookingButton?: boolean;
  isCommunity?: boolean;
  mediaVideo?: MediaVideoFragment;
  videoProgress: number;
  playing: boolean;
  setPlaying: React.Dispatch<React.SetStateAction<boolean>>;
}) => (
  <>
    <Container
      containerWidth="fluid"
      className={styles.heroContentMobileVideoButton}
    >
      {mediaVideo && (
        <div className={styles.buttonContainer}>
          <PlayButton
            progress={videoProgress}
            playing={playing}
            setPlaying={setPlaying}
          />
        </div>
      )}
    </Container>

    <Container
      containerWidth="fixed"
      className={styles.heroContentMobileContainer}
    >
      <div className={styles.heroContentMobile}>
        <HeroContent
          primaryCta={primaryCta as CtaFragment}
          heading={headingText || ''}
          description={descriptionText || ''}
          displayTourBookingButton={displayTourBookingButton}
          isCommunity={isCommunity}
        />
        <div className={styles.sunrayContainer}>
          <SunraySVG color={COLOR_LIME} />
        </div>
      </div>
    </Container>
  </>
);

const PlayButton = ({
  progress,
  playing,
  setPlaying
}: {
  progress: number;
  playing: boolean;
  setPlaying: (playing: boolean) => void;
}) => (
  <button
    aria-label={playing ? 'Pause video' : 'Play video'}
    className={styles.playButton}
    onClick={() => setPlaying(!playing)}
  >
    <div
      className={styles.progressRing}
      // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
      style={{
        background: `conic-gradient(${styles.colorouterspace} 0deg ${(
          progress * 360
        ).toFixed(1)}deg, ${styles.colorsidecar} ${(progress * 360).toFixed(
          1
        )}deg 360deg)`
      }}
    ></div>
    <div className={styles.playIconButtonWrapper}>
      <div
        className={classNames(styles.playIconWrapper, {
          [styles.playIconWrapperPlay]: !playing
        })}
      >
        <BoxIcon name={playing ? 'pause' : 'play'} size="xl" />
      </div>
    </div>
  </button>
);

const HeroLarge: React.FC<HeroLarge> = ({
  isHomepage,
  heading,
  description,
  mediaItem,
  replacementDictionary,
  primaryCta,
  isCommunity,
  displayTourBookingButton
}) => {
  const mediaImage = mediaItem?.__typename === 'MediaImage' && mediaItem;
  const mediaVideo = mediaItem?.__typename === 'MediaVideo' && mediaItem;
  const [videoProgress, setVideoProgress] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const headingText = replacementDictionary
    ? replaceStringWithDictionary(heading, replacementDictionary)
    : heading;

  const descriptionText = replacementDictionary
    ? replaceStringWithDictionary(description, replacementDictionary)
    : description;

  const updateVideoProgress = ({ played }: { played: number }) => {
    startTransition(() => {
      setVideoProgress(played);
    });
  };

  let video: CloudinaryVideo | undefined;
  if (mediaVideo) {
    video = cloudinaryClient.video(
      mediaVideo?.cloudinaryVideoSource?.[0].public_id
    );
    video.resize(fill().height(720));
  }

  const poster = mediaImage
    ? mediaImage?.cloudinarySource?.[0]?.url
    : video
        ?.videoEdit(trim().startOffset(0))
        .delivery(format('avif'))
        .quality(autoGood())
        .toURL();

  const videoConfig = {
    file: {
      attributes: {
        style: {
          height: '100%',
          width: '100%',
          objectFit: 'cover'
        },
        poster: poster,
        preload: 'none'
      }
    }
  };

  return (
    <div className={styles.hero}>
      {mediaVideo && (
        <Head>
          <link rel="preconnect" href="https://assets.levande.com.au" />
          <link rel="preload" href={poster} as="image" />
        </Head>
      )}
      {isHomepage ? (
        <Visibility xs={false} lg>
          <DesktopHeroContentContainer
            primaryCta={primaryCta as CtaFragment}
            headingText={headingText}
            descriptionText={descriptionText}
            displayTourBookingButton={displayTourBookingButton}
            isCommunity={isCommunity}
            mediaVideo={mediaVideo as MediaVideoFragment}
            videoProgress={videoProgress}
            playing={playing}
            setPlaying={setPlaying}
          />
        </Visibility>
      ) : (
        <Visibility xs={false} md>
          <DesktopHeroContentContainer
            primaryCta={primaryCta as CtaFragment}
            headingText={headingText}
            descriptionText={descriptionText}
            displayTourBookingButton={displayTourBookingButton}
            isCommunity={isCommunity}
            mediaVideo={mediaVideo as MediaVideoFragment}
            videoProgress={videoProgress}
            playing={playing}
            setPlaying={setPlaying}
          />
        </Visibility>
      )}

      {isHomepage ? (
        <Visibility xs lg={false}>
          <MobileHeroContentContainer
            primaryCta={primaryCta as CtaFragment}
            headingText={headingText}
            descriptionText={descriptionText}
            displayTourBookingButton={displayTourBookingButton}
            isCommunity={isCommunity}
            mediaVideo={mediaVideo as MediaVideoFragment}
            videoProgress={videoProgress}
            playing={playing}
            setPlaying={setPlaying}
          />
        </Visibility>
      ) : (
        <Visibility xs md={false}>
          <MobileHeroContentContainer
            primaryCta={primaryCta as CtaFragment}
            headingText={headingText}
            descriptionText={descriptionText}
            displayTourBookingButton={displayTourBookingButton}
            isCommunity={isCommunity}
            mediaVideo={mediaVideo as MediaVideoFragment}
            videoProgress={videoProgress}
            playing={playing}
            setPlaying={setPlaying}
          />
        </Visibility>
      )}

      {mediaImage && (
        <div className={styles.heroImage}>
          <MediaImage {...mediaImage} priority />
        </div>
      )}

      {mediaVideo && (
        <div
          className={classNames(styles.videoContainer, {
            [styles.videoContainerHomepage]: isHomepage
          })}
        >
          {isClient && (
            <ReactPlayer
              url={video?.delivery(format('mp4')).quality(autoGood()).toURL()}
              width="100%"
              height="100%"
              config={videoConfig}
              progressInterval={33}
              onProgress={updateVideoProgress}
              playing={playing}
              playsinline
              loop
              volume={0}
              muted
            />
          )}
        </div>
      )}
    </div>
  );
};

export default HeroLarge;
